/**
 * Router — Route definitions for AFL League Hub
 * Uses React.lazy for route-based code-splitting.
 */
import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { themes, DS } from './designSystem';
import RouteErrorBoundary from './components/RouteErrorBoundary.jsx';

// Lazy-loaded route components
const HomePage = lazy(() => import('./pages/HomePage'));
const LoginPage = lazy(() => import('./LoginPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const LeagueView = lazy(() => import('./LeagueView'));
const PublicMockDraftPage = lazy(() => import('./pages/PublicMockDraftPage'));
const SharedDraftPage = lazy(() => import('./pages/SharedDraftPage'));

const _T = themes.dark;
const AFL_LEAGUE_ID = 'a1a1a1a1-0000-0000-0000-000000000001';

/** Shared loading fallback for Suspense boundaries */
function LoadingScreen({ message = 'Loading...' }) {
  return (
    <div style={{
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      height: '100vh', background: _T.bg, color: _T.t1,
      fontFamily: DS.ff.ui,
    }}>
      <div style={{ textAlign: 'center' }}>
        <div style={{ fontSize: 24, fontWeight: DS.weight.bold, marginBottom: 8 }}>AFL Draft Board</div>
        <div style={{ fontSize: DS.font.lg, color: _T.t4 }}>{message}</div>
      </div>
    </div>
  );
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <LoadingScreen message="Checking session..." />;
  return user ? children : <Navigate to="/login" replace />;
}

function PublicRoute({ children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to={`/league/${AFL_LEAGUE_ID}`} replace /> : children;
}

export default function AppRouter() {
  return (
    <RouteErrorBoundary>
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/mock-draft" element={<PublicMockDraftPage />} />
        <Route path="/mock-draft/:sport" element={<PublicMockDraftPage />} />
        <Route path="/draft/:shareId" element={<SharedDraftPage />} />
        <Route path="/login" element={
          <PublicRoute>
            <LoginPageWrapper />
          </PublicRoute>
        } />

        {/* Protected league routes — no AppShell wrapper, AFLApp has its own nav */}
        <Route path="/league/:leagueId" element={
          <ProtectedRoute><LeagueView /></ProtectedRoute>
        }>
          {/* All sub-routes render null — useViewRoute maps URL → view ID */}
          <Route index element={null} />
          {/* Draft Room */}
          <Route path="draft" element={null} />
          <Route path="draft/rosters" element={null} />
          <Route path="draft/log" element={null} />
          {/* My Team section */}
          <Route path="my-team" element={null} />
          <Route path="my-team/keepers" element={null} />
          <Route path="my-team/stats" element={null} />
          {/* Analysis section */}
          <Route path="analysis" element={null} />
          <Route path="analysis/news" element={null} />
          <Route path="analysis/rankings" element={null} />
          <Route path="analysis/cheat-sheet" element={null} />
          <Route path="analysis/mock-draft" element={null} />
          <Route path="analysis/scarcity" element={null} />
          <Route path="analysis/keepers" element={null} />
          <Route path="analysis/draft-grades" element={null} />
          <Route path="analysis/leaderboard" element={null} />
          <Route path="analysis/team-stats" element={null} />
          {/* Trades section */}
          <Route path="trades" element={null} />
          <Route path="trades/history" element={null} />
          <Route path="trades/block" element={null} />
          {/* Settings & commissioner */}
          <Route path="settings" element={null} />
          <Route path="commissioner" element={null} />
          <Route path="checklist" element={null} />
          <Route path="keepers" element={null} />
          <Route path="keeper-manager" element={null} />
          <Route path="keeper-announce" element={null} />
          <Route path="season-rosters" element={null} />
          {/* In-season (preserved for future) */}
          <Route path="standings" element={null} />
          <Route path="scoreboard" element={null} />
          <Route path="matchup" element={null} />
          <Route path="waivers" element={null} />
          <Route path="messages" element={null} />
          <Route path="activity" element={null} />
          <Route path="playoffs" element={null} />
          <Route path="home" element={null} />
          {/* Team roster view */}
          <Route path="team/:teamName" element={null} />
          {/* Legacy redirects — old flat paths redirect to new section paths */}
          <Route path="rankings" element={<Navigate to="analysis/rankings" replace />} />
          <Route path="cheat-sheet" element={<Navigate to="analysis/cheat-sheet" replace />} />
          <Route path="mock-draft" element={<Navigate to="analysis/mock-draft" replace />} />
          <Route path="scarcity" element={<Navigate to="analysis/scarcity" replace />} />
          <Route path="draft-grades" element={<Navigate to="analysis/draft-grades" replace />} />
          <Route path="leaderboard" element={<Navigate to="analysis/leaderboard" replace />} />
          <Route path="team-stats" element={<Navigate to="analysis/team-stats" replace />} />
          <Route path="news" element={<Navigate to="analysis/news" replace />} />
          <Route path="trade-block" element={<Navigate to="trades/block" replace />} />
          <Route path="*" element={null} />
        </Route>

        {/* Legacy redirects */}
        <Route path="/dashboard" element={<Navigate to={`/league/${AFL_LEAGUE_ID}`} replace />} />
        <Route path="/import/*" element={<Navigate to={`/league/${AFL_LEAGUE_ID}`} replace />} />
        <Route path="/account" element={
          <ProtectedRoute><AccountPage /></ProtectedRoute>
        } />
        <Route path="/join/:inviteCode" element={<Navigate to={`/league/${AFL_LEAGUE_ID}`} replace />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </Suspense>
    </RouteErrorBoundary>
  );
}

/** LoginPage needs auth functions passed in */
function LoginPageWrapper() {
  const { signIn, signUp, signInWithMagicLink, resetPassword } = useAuth();
  return <LoginPage signIn={signIn} signUp={signUp} signInWithMagicLink={signInWithMagicLink} resetPassword={resetPassword} />;
}
